/**
 * Normalisation des numéros de téléphone, sans dépendance (pas de zod) : importée
 * à la fois par les schémas serveur (phone.ts) et par les formulaires client
 * (components/mre/lead-request.ts, components/forms/ProConseillerForm.tsx), pour
 * que le navigateur et les routes API appliquent exactement la même règle
 * (06/10/2026).
 */

/** Marques bidi invisibles qu'ajoutent certains copier-coller (U+200E/F, U+202A–E, U+2066–9). */
const BIDI_MARKS = /[\u200e\u200f\u202a-\u202e\u2066-\u2069]/g;

/**
 * Chiffres arabes-indiens (U+0660–0669) et persans (U+06F0–06F9). Un iPhone
 * réglé en arabe les tape dans un champ téléphone : refusés tels quels jusqu'au
 * 21/10/2026 (7 essais perdus sur pro.sahha.ma/ar/contact le 14/10).
 */
const EASTERN_DIGITS = /[\u0660-\u0669\u06f0-\u06f9]/g;

/**
 * Séparateurs retirés : espaces (dont insécables), tirets (dont U+2010–2015 et
 * le signe moins U+2212), points, parenthèses, barres obliques.
 */
const PHONE_SEPARATORS = /[\s\-\u2010-\u2015\u2212().\/]/g;

/** Chiffres arabes-indiens et persans → chiffres ASCII ; le reste est inchangé. */
export function toAsciiDigits(raw: string): string {
  return raw.replace(EASTERN_DIGITS, (d) => {
    const code = d.charCodeAt(0);
    return String(code - (code >= 0x06f0 ? 0x06f0 : 0x0660));
  });
}

/** Saisie brute → chiffres ASCII, « + » pleine chasse (U+FF0B) ramené à « + », sans marques bidi. */
function asciiPhone(raw: string): string {
  return toAsciiDigits(raw.replace(BIDI_MARKS, "")).replace(/\uff0b/g, "+");
}

/**
 * Numéro marocain : 0 suivi de 5/6/7 et 8 chiffres, ou indicatif 212 sous ses
 * formes courantes — « +212… », « 00212… » (format saisi par les deux médecins
 * perdus du 15/10 : « 00212717173088 », « 00212 6 61 41 22 22 »), « 212… »
 * (format WhatsApp, 12 chiffres : aucun numéro national n'a cette forme) et le
 * 0 national gardé après l'indicatif (« +212 06… », « +212 (0)6… »).
 * Sortie inchangée : toujours « +212 » + 9 chiffres.
 */
const MAR_PHONE_REGEX = /^(?:(?:\+|00)?2120?|0)([5-7]\d{8})$/;

export function normalizeMarPhone(raw: string): string | null {
  const cleaned = asciiPhone(raw).replace(PHONE_SEPARATORS, "");
  const match = cleaned.match(MAR_PHONE_REGEX);
  if (!match) return null;
  return `+212${match[1]}`;
}

/* ─── Numéros internationaux (Marocains du monde) ──────────────────────── */

/** E.164 : « + », indicatif pays (premier chiffre ≠ 0), 8 à 15 chiffres au total. */
const INTL_PHONE_REGEX = /^\+[1-9]\d{7,14}$/;

/**
 * Numéro international au format E.164, normalisé en « +… ». Réservé aux leads
 * « Marocains du monde » (types MRE_* de /api/leads) : le devis MRE demande
 * « Téléphone (+33, +32, +1…) » mais phoneMar refusait tout numéro étranger
 * (bug du 06/10/2026 : demande perdue).
 *
 * - Séparateurs retirés comme normalizeMarPhone (espaces, points, tirets,
 *   parenthèses, barres obliques) et chiffres arabes-indiens convertis, ainsi
 *   que le « (0) » de la notation « +33 (0)6 12 34 56 78 » ou « +44 (0)20… » :
 *   ce 0 national ne se compose pas depuis l'étranger, le garder stockerait un
 *   numéro faux (+330612345678).
 * - « 00 » initial = préfixe international de l'Europe et de l'Afrique, où vit
 *   la majorité des MRE (France, Belgique, Pays-Bas, Espagne, Italie,
 *   Allemagne…) : « 0033 6 12 34 56 78 » est NORMALISÉ en « +33612345678 », pas
 *   refusé (décision du 06/10/2026 : format courant et sans ambiguïté, aucun
 *   numéro national ne commence par 00). « 011 » (Amérique du Nord) n'est pas
 *   reconnu : le formulaire demande « +1… ».
 * - Indicatif +212 : le numéro doit rester un numéro marocain valide (règle
 *   phoneMar), sinon « +21261234567 » (un chiffre oublié) passerait pour un
 *   numéro étranger de 11 chiffres.
 * - Sans « + » ni « 00 » : refusé ici. normalizeMarOrIntlPhone applique d'abord
 *   la règle marocaine : « 06 12 34 56 78 » reste un mobile marocain (un 06
 *   français sans +33 en est indiscernable, d'où le « +33… » du libellé).
 */
export function normalizeIntlPhone(raw: string): string | null {
  const compact = asciiPhone(raw).replace(/\(0\)/g, "").replace(PHONE_SEPARATORS, "");
  const e164 = compact.startsWith("00") ? `+${compact.slice(2)}` : compact;
  if (!INTL_PHONE_REGEX.test(e164)) return null;
  return e164.startsWith("+212") ? normalizeMarPhone(e164) : e164;
}

/** Numéro marocain (règle phoneMar) ou international (normalizeIntlPhone), normalisé en « +… ». */
export function normalizeMarOrIntlPhone(raw: string): string | null {
  return normalizeMarPhone(raw) ?? normalizeIntlPhone(raw);
}
